// src/ConsumerApp/DesignSystem/Navigation/Footer.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import CenteredContainer from '../Layout/CenteredContainer';
import DirectorySeoSection from './components/DirectorySeoSection';
import { theme } from '../../../assets/theme/theme';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.brickbucket.buyer';
const YOUTUBE_URL = 'https://www.youtube.com/@brickbucketplatform';
const INSTAGRAM_URL = 'https://www.instagram.com/brickbucketplatform';
const FACEBOOK_URL = 'https://www.facebook.com/brickbucketplatform';
const LINKEDIN_URL = 'https://www.linkedin.com/company/brickbucket';

export default function Footer() {
  const navigate = useNavigate();

  return (
    <footer style={styles.footerShell}>
      <style>{`
        .bb-footer-container {
          display: flex;
          flex-direction: column;
          gap: 32px;
          padding: 44px 0 32px 0;
          width: 100%;
        }

        /* ── 4-Column Directory Grid ── */
        .bb-footer-grid {
          display: grid;
          grid-template-columns: 1.6fr 1fr 1.2fr 1.2fr;
          gap: 36px;
          width: 100%;
          align-items: start;
        }

        .bb-brand-text-logo {
          font-family: ${theme.typography?.headingMD?.fontFamily || 'sans-serif'};
          font-size: 26px;
          font-weight: 900;
          letter-spacing: -0.6px;
          color: #0F172A;
          cursor: pointer;
          user-select: none;
          line-height: 1;
        }

        .bb-footer-link {
          font-size: 13.5px;
          color: #475569;
          text-decoration: none;
          line-height: 22px;
          cursor: pointer;
          transition: color 0.15s ease;
          display: inline-block;
          font-weight: 500;
          background: none;
          border: none;
          padding: 0;
          text-align: left;
        }

        .bb-footer-link:hover {
          color: #E9001E;
        }

        .bb-social-icon-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748B;
          text-decoration: none;
          transition: transform 0.15s ease, color 0.15s ease;
        }

        .bb-social-icon-btn:hover {
          color: #0F172A;
          transform: translateY(-2px);
        }

        .bb-app-badge-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          max-width: 195px;
          height: 44px;
          padding: 0 14px;
          background-color: #FFFFFF;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          text-decoration: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: border-color 0.15s ease;
          box-sizing: border-box;
        }

        .bb-app-badge-btn:hover {
          border-color: #94A3B8;
        }

        @media (max-width: 992px) {
          .bb-footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 28px;
          }
        }

        @media (max-width: 640px) {
          .bb-footer-container {
            padding: 28px 0 20px 0;
            gap: 20px;
          }
          .bb-footer-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
        }
      `}</style>

      <CenteredContainer>
        <div className="bb-footer-container">
          {/* Reusable Directory Section (Popular Searches, Categories, Cities) */}
          <DirectorySeoSection />

          {/* Main Footer Links */}
          <div className="bb-footer-grid">
            {/* Column 1: Brand & Social Media */}
            <div style={styles.brandCol}>
              <div
                className="bb-brand-text-logo"
                onClick={() => navigate('/consumer/HomeScreen')}
                title="Brickbucket"
              >
                Brickbucket
              </div>

              <div style={styles.socialRow}>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bb-social-icon-btn"
                  title="Instagram"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bb-social-icon-btn"
                  title="YouTube"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                </a>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bb-social-icon-btn"
                  title="Facebook"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bb-social-icon-btn"
                  title="LinkedIn"
                >
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Column 2: Navigation Links */}
            <div style={styles.linksCol}>
              <button
                type="button"
                className="bb-footer-link"
                onClick={() => navigate('/consumer/HomeScreen')}
              >
                Home
              </button>
              <a href="/company.html" className="bb-footer-link">
                About Us
              </a>
              <a href="/careers.html" className="bb-footer-link">
                Careers
              </a>
              <button
                type="button"
                className="bb-footer-link"
                onClick={() => navigate('/consumer/account/support')}
              >
                Customer Support
              </button>
              <a href="/press.html" className="bb-footer-link">
                Press
              </a>
              <a href="/blog.html" className="bb-footer-link">
                Blog
              </a>
            </div>

            {/* Column 3: Policy & Business Links */}
            <div style={styles.linksCol}>
              <button
                type="button"
                className="bb-footer-link"
                onClick={() => navigate('/consumer/account/policies')}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                className="bb-footer-link"
                onClick={() => navigate('/consumer/account/policies')}
              >
                Terms of Use
              </button>
              <a href="/seller.html" className="bb-footer-link">
                Sell on Brickbucket
              </a>
              <a href="/deliver.html" className="bb-footer-link">
                Deliver with Brickbucket
              </a>
              <a href="/franchise.html" className="bb-footer-link">
                Franchise with Brickbucket
              </a>
              <a href="/investors.html" className="bb-footer-link">
                Investor Relations
              </a>
            </div>

            {/* Column 4: App Download Badges */}
            <div style={styles.appDownloadCol}>
              <h4 style={styles.appDownloadHeading}>Download App</h4>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bb-app-badge-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3.6 1.8L13.8 12 3.6 22.2c-.4-.4-.6-.9-.6-1.4V3.2c0-.5.2-1 .6-1.4z" fill="#4285F4" />
                  <path d="M17.4 8.4l-3.6 3.6 3.6 3.6 4.1-2.4c1.2-.7 1.2-1.8 0-2.4l-4.1-2.4z" fill="#FBBC04" />
                  <path d="M3.6 1.8l10.2 10.2 3.6-3.6-12.4-7.1c-.5-.3-1-.1-1.4.5z" fill="#EA4335" />
                  <path d="M13.8 12l-10.2 10.2c.4.6.9.8 1.4.5l12.4-7.1-3.6-3.6z" fill="#34A853" />
                </svg>
                <span style={styles.badgeText}>Get it on play store</span>
              </a>

              <div
                className="bb-app-badge-btn"
                style={{ cursor: 'default', opacity: 0.9 }}
                title="iOS App Coming Soon"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.85-.9.04-2 .6-2.65 1.35-.58.66-1.09 1.73-.96 2.76 1.01.08 2.06-.51 2.69-1.26z" />
                </svg>
                <span style={styles.badgeText}>Get it on app store</span>
              </div>
            </div>
          </div>
        </div>
      </CenteredContainer>
    </footer>
  );
}

const styles = {
  footerShell: {
    backgroundColor: '#FFFFFF',
    borderTop: `1px solid ${theme.colors?.grey005 || '#E2E8F0'}`,
    width: '100%',
    marginTop: 'auto',
    fontFamily: theme.typography?.bodyMD?.fontFamily || 'sans-serif',
  },
  brandCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 14,
  },
  socialRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 2,
  },
  linksCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'flex-start',
  },
  appDownloadCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },
  appDownloadHeading: {
    fontSize: 14,
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 2px 0',
  },
  badgeText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#1E293B',
    whiteSpace: 'nowrap',
  },
};